/**
 * T978 — the iOS launch images of the installed app, one per screen class and orientation.
 *
 * iOS shows an `apple-touch-startup-image` only when its `media` query names the device's
 * exact CSS size and pixel ratio; with no match it shows a blank screen until the first
 * paint. iPhones open the installed app in portrait; an iPad opens it in whichever
 * orientation its home screen is in (Safari does not apply the manifest's orientation lock),
 * so every iPad class has a landscape image too. `device-width` / `device-height` name the
 * screen's portrait size in both orientations; only `orientation` and the PNG's size change.
 * `scripts/maintenance/generate-startup-images.mjs` renders one PNG per entry and
 * `app/root.tsx` emits one link per entry — both read this list.
 */

export type StartupImage = {
  /** The screen's CSS size in portrait (iOS's `device-width` / `device-height`). */
  readonly width: number;
  readonly height: number;
  /** `-webkit-device-pixel-ratio`. */
  readonly ratio: number;
  readonly orientation: "portrait" | "landscape";
};

type Screen = Omit<StartupImage, "orientation">;

/** Bump with the artwork, the way root's icon links carry T735's stamp. */
export const STARTUP_IMAGE_STAMP = "v=1";

const IPHONES: readonly Screen[] = [
  { width: 320, height: 568, ratio: 2 }, // SE (1st gen)
  { width: 375, height: 667, ratio: 2 }, // SE (2nd/3rd gen), 6–8
  { width: 414, height: 736, ratio: 3 }, // 6–8 Plus
  { width: 375, height: 812, ratio: 3 }, // X, XS, 11 Pro, 12 mini, 13 mini
  { width: 414, height: 896, ratio: 2 }, // XR, 11
  { width: 414, height: 896, ratio: 3 }, // XS Max, 11 Pro Max
  { width: 390, height: 844, ratio: 3 }, // 12, 12 Pro, 13, 13 Pro, 14, 16e
  { width: 428, height: 926, ratio: 3 }, // 12 Pro Max, 13 Pro Max, 14 Plus
  { width: 393, height: 852, ratio: 3 }, // 14 Pro, 15, 15 Pro, 16
  { width: 430, height: 932, ratio: 3 }, // 14 Pro Max, 15 Plus, 15 Pro Max, 16 Plus
  { width: 402, height: 874, ratio: 3 }, // 16 Pro, 17, 17 Pro
  { width: 420, height: 912, ratio: 3 }, // Air
  { width: 440, height: 956, ratio: 3 }, // 16 Pro Max, 17 Pro Max
];

const IPADS: readonly Screen[] = [
  { width: 744, height: 1133, ratio: 2 }, // mini (6th/7th gen)
  { width: 768, height: 1024, ratio: 2 }, // 9.7", mini (5th gen)
  { width: 810, height: 1080, ratio: 2 }, // 10.2"
  { width: 820, height: 1180, ratio: 2 }, // 10.9" (Air 4/5, 10th gen)
  { width: 834, height: 1112, ratio: 2 }, // Air (3rd gen), Pro 10.5"
  { width: 834, height: 1194, ratio: 2 }, // Pro 11"
  { width: 834, height: 1210, ratio: 2 }, // Pro 11" (M4)
  { width: 1024, height: 1366, ratio: 2 }, // Pro 12.9"
  { width: 1032, height: 1376, ratio: 2 }, // Pro 13" (M4), Air 13"
];

export const STARTUP_IMAGES: readonly StartupImage[] = [
  ...IPHONES.map((screen) => ({ ...screen, orientation: "portrait" as const })),
  ...IPADS.flatMap((screen) => [
    { ...screen, orientation: "portrait" as const },
    { ...screen, orientation: "landscape" as const },
  ]),
];

/** The screen's CSS size as the image is shown (portrait or landscape). */
export function startupImageViewport(image: StartupImage): { width: number; height: number } {
  return image.orientation === "portrait"
    ? { width: image.width, height: image.height }
    : { width: image.height, height: image.width };
}

/** The PNG's pixel size. */
export function startupImagePixels(image: StartupImage): { width: number; height: number } {
  const { width, height } = startupImageViewport(image);
  return { width: width * image.ratio, height: height * image.ratio };
}

/** Public path of the PNG, named by its pixel size (unique across the list: a landscape image swaps the two). */
export function startupImageFile(image: StartupImage): string {
  const { width, height } = startupImagePixels(image);
  return `/startup/${width}x${height}.png`;
}

/** An iPad class: the only screens with a landscape image (`device-width` ≥ 700). */
function isTabletStartupImage(image: StartupImage): boolean {
  return image.width >= 700;
}

/**
 * The media query iOS matches when the app is added to the Home Screen. An iPhone query names
 * no orientation: iPhones open the app in portrait only, and on iOS 26 a phone query carrying
 * `(orientation: portrait)` left her iPhone on a black launch (T978 QA, 2026-11-03), while the
 * same size without it is the form reported working there. An iPad query keeps the orientation:
 * it is what tells its two images apart.
 */
export function startupImageMedia(image: StartupImage): string {
  const size =
    `(device-width: ${image.width}px) and (device-height: ${image.height}px) and ` +
    `(-webkit-device-pixel-ratio: ${image.ratio})`;
  return isTabletStartupImage(image) ? `${size} and (orientation: ${image.orientation})` : size;
}
